// =============================================================================
// promptTemplates.ts — generates the prompt that Jamie pastes into Claude.ai
// to draft a batch of items for a given (test, sector, theme, difficulty).
//
// The prompt instructs the model to return STRICT JSON matching the
// psychometric_test_items schema for that engine. Validation in
// itemValidation.ts catches anything malformed before it reaches the DB.
//
// Each template includes:
//   1. The test format + scoring guidance
//   2. The exact JSON schema expected
//   3. 1-2 worked example items so the model has a concrete pattern
//   4. Constraints (sector flavour, difficulty band, IP policy)
// =============================================================================

export interface PromptArgs {
  testSlug: string;
  testName: string;
  sectors: string[];
  theme: string;
  difficulty: 1 | 2 | 3;
  count: number;
  /** Optional firm name for marquee firm-specific items (becomes a firm-<slug> tag). */
  firmTag?: string | null;
}

const SECTOR_FLAVOUR_NOTES: Record<string, string> = {
  'banking':         'investment banking, capital markets, M&A',
  'banking-ibd':     'investment banking analyst work — pitches, models, M&A volumes, ECM/DCM data',
  'banking-markets': 'sales & trading, FX/rates/equities, market data, P&L, risk metrics',
  'banking-quant':   'quant trading, hedge funds, systematic strategies, statistical arbitrage',
  'banking-pe':      'private equity, deal sourcing, fund performance, exit multiples',
  'banking-vc':      'venture capital, startup metrics, ARR/MRR, runway, cap tables',
  'banking-am':      'asset management, AUM, fund flows, benchmark performance',
  'banking-retail':  'retail banking, consumer products, branch network, mortgages',
  'consulting':      'management consulting — strategy, market sizing, benchmarking',
  'consulting-mbb':  'top-tier strategy consulting (McKinsey/BCG/Bain) — market entry, growth strategy',
  'consulting-strategy': 'strategy houses — competitive positioning, M&A diligence',
  'big4':            'Big 4 advisory + audit — risk frameworks, compliance, transaction services',
  'big4-audit':      'external/internal audit — financial controls, materiality, sampling',
  'big4-tax':        'tax advisory — corporate tax, transfer pricing, indirect tax',
  'big4-advisory':   'Big 4 advisory work — deals, restructuring, business consulting',
  'law':             'commercial law — contracts, regulatory, dispute resolution',
  'law-magic-circle':'Magic Circle law firms (A&O Shearman, Clifford Chance, Freshfields, Linklaters, Slaughter & May)',
  'tech':            'tech / engineering — product metrics, system performance, A/B testing',
  'tech-faang':      'big tech (Google/Meta/Amazon/Apple/Microsoft/Netflix) interview-style problems',
  'tech-fintech':    'fintech product roles — payments, neobanks, regtech',
  'civil-service':   'UK Civil Service Fast Stream — policy, public service delivery',
  'government':      'wider government / NHS / regulators — public-sector context',
  'industry':        'general corporate / industry analyst roles',
  'retail':          'retail and consumer goods — sales, footfall, basket size, churn',
  'fmcg':            'FMCG / consumer brands — market share, pricing, brand health',
  'energy':          'energy and utilities — generation mix, capacity, ESG metrics',
  'media':           'media and advertising — reach, CPM, audience metrics',
  'generic':         'broadly applicable corporate context with no strong industry tilt',
};

function describeSectors(sectors: string[]): string {
  if (sectors.length === 0) return 'broadly applicable corporate context';
  return sectors.map((s) => SECTOR_FLAVOUR_NOTES[s] ?? s).join('; ');
}

const DIFFICULTY_NOTES: Record<1 | 2 | 3, string> = {
  1: 'Easy — accessible for first-year undergraduates with no prior practice. One calculation step or one inference.',
  2: 'Medium — typical graduate-cohort target difficulty. Two-step calculations or two layers of inference.',
  3: 'Hard — top-of-cohort. Multi-step calculations, edge-case data, or trick distractors that look plausible.',
};

// ─── SJT ────────────────────────────────────────────────────────────────
const SJT_TEMPLATE = `You are drafting situational-judgement test items for Intervyo, a UK graduate-prep platform. Each item is an original recreation of the SHL Most/Least format — never copy from any real test.

Each item is a workplace scenario with 4 possible responses. The user picks the MOST effective response and the LEAST effective response. There's a clear "best" and a clear "worst" but the middle two should be plausibly defensible (so the test reads judgement, not pattern-matching).

CONTEXT FLAVOUR: {{sectors}}
THEME / TOPIC: {{theme}}
DIFFICULTY: {{difficulty}} — {{difficultyNote}}
COUNT: {{count}}

OUTPUT FORMAT — return ONLY a JSON array. No prose, no markdown fences, just the array. Each item:
{
  "context": "<the scenario, 2-4 sentences, names a specific role like 'junior analyst' and a specific situation>",
  "prompt": "Pick the most and least effective action.",
  "options": [
    { "id": "a", "text": "<plausible action>" },
    { "id": "b", "text": "<plausible action>" },
    { "id": "c", "text": "<plausible action>" },
    { "id": "d", "text": "<plausible action>" }
  ],
  "correct_answer": "{\\"most\\":\\"b\\",\\"least\\":\\"d\\"}",
  "scoring_metadata": {
    "sectors": {{sectorsJson}},
    "competency": "<one of: judgement, integrity, commercial_awareness, teamwork, communication, resilience>",
    "difficulty": {{difficulty}},
    "hint": "<a short coaching nudge — what to focus on, no spoilers>",
    "explanation": "<2-3 sentences explaining why the 'most' is best and the 'least' is worst, naming both option letters>"
  }
}

CONSTRAINTS:
- Scenarios must be original — no real firm names, no real news events.
- 'Most effective' should clearly satisfy a workplace competency (not a personal preference).
- 'Least effective' should violate a clear professional norm.
- The two middle options should be plausibly defensible but suboptimal.
- Sector flavour should be visible in the scenario (e.g. 'spreadsheet model' for banking, 'client-facing memo' for consulting).
- Output ONLY the JSON array.`;

// ─── Watson-Glaser ────────────────────────────────────────────────────
const WATSON_TEMPLATE = `You are drafting Watson-Glaser style critical-thinking items for Intervyo. Each item is an original recreation — never copy from any real Pearson/TalentLens material.

Watson-Glaser has 5 sections. For this batch, ALL items are for the same section: {{section}}.

Section: {{section}}
Section rules:
{{sectionRules}}

CONTEXT FLAVOUR: {{sectors}}
THEME / TOPIC: {{theme}}
DIFFICULTY: {{difficulty}} — {{difficultyNote}}
COUNT: {{count}}

OUTPUT FORMAT — return ONLY a JSON array. Each item:
{
  "context": "<a short reading passage, 2-4 sentences. Realistic but anonymised. Self-contained — judge ONLY from the passage.>",
  "prompt": "<a single statement OR question that the user judges using the section's answer scheme>",
  "options": {{sectionOptions}},
  "correct_answer": "<one of the option ids>",
  "scoring_metadata": {
    "section": "{{section}}",
    "sectors": {{sectorsJson}},
    "difficulty": {{difficulty}},
    "hint": "<a short coaching nudge>",
    "explanation": "<2-3 sentences explaining why this answer is correct under the section's rules>"
  }
}

CONSTRAINTS:
- Passages must be ORIGINAL. Names, organisations, statistics — all fictional.
- Each item must be solvable using ONLY the passage. No outside knowledge.
- The classic trap: applying the wrong section's rules. Make sure the right answer aligns with this section's scheme.
- Output ONLY the JSON array.`;

// ─── Cappfinity Stage Numerical ───────────────────────────────────────
const CAPPFINITY_NUM_TEMPLATE = `You are drafting Cappfinity Stage numerical reasoning items for Intervyo. Original recreations — never copy from real Cappfinity material.

Each item presents a chart or table of figures and asks a numerical question. The chart data should be self-contained (the user shouldn't need outside knowledge). Difficulty controls how many calculation steps are needed.

CONTEXT FLAVOUR: {{sectors}}
THEME / TOPIC: {{theme}}
DIFFICULTY: {{difficulty}} — {{difficultyNote}}
COUNT: {{count}}
STAGE DISTRIBUTION: distribute items roughly evenly across stages 1, 2, 3 (set scoring_metadata.stage = 1 | 2 | 3). Stage 1 should be easier than stage 3.

OUTPUT FORMAT — return ONLY a JSON array. Each item:
{
  "context": "<introduces the chart/table data needed for the question, 1-2 sentences>",
  "prompt": "<the specific numerical question to answer>",
  "options": [
    { "id": "a", "text": "<numeric option, formatted with units e.g. '£1,400,000' or '37%'>" },
    { "id": "b", "text": "..." },
    { "id": "c", "text": "..." },
    { "id": "d", "text": "..." },
    { "id": "e", "text": "..." }
  ],
  "correct_answer": "<one of a|b|c|d|e>",
  "scoring_metadata": {
    "stage": 1,
    "chartType": "<one of: bar | stacked-bar | line | pie | table>",
    "chartConfig": {
      "title": "<chart title>",
      "xKey": "<x-axis field name>",
      "yKeys": ["<y-axis field name 1>", "<y-axis field name 2>"],
      "data": [
        { "<xKey>": "<value>", "<yKey1>": <number>, "<yKey2>": <number> }
      ]
    },
    "sectors": {{sectorsJson}},
    "difficulty": {{difficulty}},
    "hint": "<calculation strategy — what to compute first>",
    "explanation": "<full working showing how to derive the correct answer>"
  }
}

CONSTRAINTS:
- All numbers in the chartConfig.data must be self-consistent so the answer is verifiable.
- Distractors must be plausibly-wrong calculations — common errors like wrong percentage base, off-by-one, transposed digits.
- Currency: use £ for UK context unless the sector specifies otherwise.
- Output ONLY the JSON array.`;

// ─── Cappfinity Stage Verbal ──────────────────────────────────────────
const CAPPFINITY_VRB_TEMPLATE = `You are drafting Cappfinity Stage verbal reasoning items for Intervyo. Original recreations only.

Each item is a short reading passage followed by a True / False / Cannot Say judgement on a statement about the passage. Cappfinity's signature is staged blocks — distribute across stages 1, 2, 3.

CONTEXT FLAVOUR: {{sectors}}
THEME / TOPIC: {{theme}}
DIFFICULTY: {{difficulty}} — {{difficultyNote}}
COUNT: {{count}}

OUTPUT FORMAT — return ONLY a JSON array. Each item:
{
  "context": "<reading passage, 4-7 sentences. Self-contained. Plausible business / industry context but original.>",
  "prompt": "<the statement to judge against the passage>",
  "options": [
    { "id": "true",        "text": "True" },
    { "id": "false",       "text": "False" },
    { "id": "cannot_say",  "text": "Cannot say" }
  ],
  "correct_answer": "<true | false | cannot_say>",
  "scoring_metadata": {
    "stage": 1,
    "sectors": {{sectorsJson}},
    "difficulty": {{difficulty}},
    "hint": "<what to look for in the passage — keyword strategy>",
    "explanation": "<which sentence in the passage supports / refutes / leaves-undetermined the statement>"
  }
}

CONSTRAINTS:
- 'Cannot say' must be the right answer for ~30% of items — never let users dismiss it.
- Trap items: use phrases that LOOK like they're answered but aren't (e.g. passage says 'most clients' but statement says 'all clients').
- Output ONLY the JSON array.`;

// ─── Watson-Glaser section configs ────────────────────────────────────
const WG_SECTIONS = {
  inference: {
    name: 'Inference',
    rules: [
      '- True: the conclusion follows definitely from the passage.',
      '- Probably True: more likely than not given the passage, but not certain.',
      '- Insufficient Data: the passage doesn\'t contain enough information to judge.',
      '- Probably False: more likely false than true given the passage.',
      '- False: the passage clearly contradicts the conclusion.',
    ],
    options: `[
    { "id": "true",            "text": "True" },
    { "id": "probably_true",   "text": "Probably True" },
    { "id": "insufficient",    "text": "Insufficient Data" },
    { "id": "probably_false",  "text": "Probably False" },
    { "id": "false",           "text": "False" }
  ]`,
  },
  assumptions: {
    name: 'Recognition of Assumptions',
    rules: [
      '- Assumption Made: the statement assumes something for the conclusion to follow.',
      '- Assumption Not Made: the statement does NOT depend on this assumption.',
    ],
    options: `[
    { "id": "made",      "text": "Assumption Made" },
    { "id": "not_made",  "text": "Assumption Not Made" }
  ]`,
  },
  deduction: {
    name: 'Deduction',
    rules: [
      '- Conclusion Follows: the conclusion necessarily follows from the premises (treat them as true).',
      '- Conclusion Does Not Follow: it does not necessarily follow.',
    ],
    options: `[
    { "id": "follows",      "text": "Conclusion Follows" },
    { "id": "does_not",     "text": "Conclusion Does Not Follow" }
  ]`,
  },
  interpretation: {
    name: 'Interpretation',
    rules: [
      '- Conclusion Follows: the conclusion follows beyond reasonable doubt from the passage.',
      '- Conclusion Does Not Follow: it does not follow beyond reasonable doubt.',
    ],
    options: `[
    { "id": "follows",      "text": "Conclusion Follows" },
    { "id": "does_not",     "text": "Conclusion Does Not Follow" }
  ]`,
  },
  arguments: {
    name: 'Evaluating Arguments',
    rules: [
      '- Strong: the argument is both important AND directly related to the question.',
      '- Weak: the argument is unimportant, off-topic, or based on irrelevant grounds.',
    ],
    options: `[
    { "id": "strong",   "text": "Strong" },
    { "id": "weak",     "text": "Weak" }
  ]`,
  },
};

export type WatsonGlaserSection = keyof typeof WG_SECTIONS;

// ─── Talent Q (adaptive numerical / verbal / logical) ────────────────
const TALENTQ_NUM_TEMPLATE = `You are drafting Talent Q Elements numerical reasoning items for Intervyo. Original recreations only.

Talent Q is adaptive — items get harder or easier based on the user's recent answers. Each item is a chart-or-table-based numerical reasoning question with 4-5 options. Difficulty controls calculation complexity.

CONTEXT FLAVOUR: {{sectors}}
THEME / TOPIC: {{theme}}
DIFFICULTY: {{difficulty}} — {{difficultyNote}}
COUNT: {{count}}

OUTPUT FORMAT — return ONLY a JSON array. Each item:
{
  "context": "<introduces the chart/table data, 1-2 sentences>",
  "prompt": "<specific numerical question>",
  "options": [
    { "id": "a", "text": "<numeric option with units>" },
    { "id": "b", "text": "..." },
    { "id": "c", "text": "..." },
    { "id": "d", "text": "..." },
    { "id": "e", "text": "..." }
  ],
  "correct_answer": "<one of a|b|c|d|e>",
  "scoring_metadata": {
    "chartType": "<one of: bar | stacked-bar | line | pie | table>",
    "chartConfig": {
      "title": "<chart title>",
      "xKey": "<x-axis field name>",
      "yKeys": ["<y-axis field name 1>", "<y-axis field name 2>"],
      "data": [{ "<xKey>": "<value>", "<yKey1>": <number>, "<yKey2>": <number> }]
    },
    "sectors": {{sectorsJson}},
    "difficulty": {{difficulty}},
    "hint": "<calculation strategy>",
    "explanation": "<full working>"
  }
}

CONSTRAINTS:
- Distractors must reflect plausible calculation errors (wrong base, off-by-one, transposed digits).
- Difficulty {{difficulty}} ranges from straightforward percentage reads (1) to multi-step compound calculations with derived totals (3).
- Output ONLY the JSON array.`;

const TALENTQ_VRB_TEMPLATE = `You are drafting Talent Q Elements verbal reasoning items for Intervyo. Original recreations only.

Each item is a short reading passage followed by a True / False / Cannot Say judgement.

CONTEXT FLAVOUR: {{sectors}}
THEME / TOPIC: {{theme}}
DIFFICULTY: {{difficulty}} — {{difficultyNote}}
COUNT: {{count}}

OUTPUT FORMAT — return ONLY a JSON array. Each item:
{
  "context": "<reading passage, 4-7 sentences. Self-contained. Original.>",
  "prompt": "<the statement to judge>",
  "options": [
    { "id": "true",       "text": "True" },
    { "id": "false",      "text": "False" },
    { "id": "cannot_say", "text": "Cannot say" }
  ],
  "correct_answer": "<true | false | cannot_say>",
  "scoring_metadata": {
    "sectors": {{sectorsJson}},
    "difficulty": {{difficulty}},
    "hint": "<keyword strategy>",
    "explanation": "<which sentence supports/refutes/leaves-undetermined>"
  }
}

CONSTRAINTS:
- ~30% of correct answers should be 'cannot_say'.
- Trap items use "most" vs "all", "many" vs "few" — words that LOOK like they answer but don't.
- Output ONLY the JSON array.`;

const TALENTQ_LOG_TEMPLATE = `You are drafting Talent Q Elements logical / abstract reasoning items for Intervyo. Original recreations only.

Each item shows a sequence of figures + 4-5 candidates; pick the figure that completes the pattern. Patterns can be in colour, shape, count, position, fill, or transformation.

CONTEXT FLAVOUR: {{sectors}}
DIFFICULTY: {{difficulty}} — {{difficultyNote}}
COUNT: {{count}}

OUTPUT FORMAT — return ONLY a JSON array. Each item:
{
  "prompt": "Which figure completes the sequence?",
  "context": "<text description of the sequence shown — for now this is text-only since figure generation isn't wired in yet, e.g. 'Three squares in a row, then two squares, then one square. Pattern continues.'>",
  "options": [
    { "id": "a", "text": "<text description of candidate>" },
    { "id": "b", "text": "..." },
    { "id": "c", "text": "..." },
    { "id": "d", "text": "..." }
  ],
  "correct_answer": "<one of a|b|c|d>",
  "scoring_metadata": {
    "sectors": {{sectorsJson}},
    "difficulty": {{difficulty}},
    "patternType": "<one of: count | colour | rotation | shape-substitution | grid-position>",
    "hint": "<what to look for>",
    "explanation": "<the rule and why this candidate fits>"
  }
}

CONSTRAINTS:
- Pattern must be derivable from the description alone — no implicit visual context.
- Distractors should match the pattern in some other axis (so they're plausible) but break on the rule axis.
- Output ONLY the JSON array.`;

// ─── Hogan HPI / HDS (Likert with construct loadings) ─────────────────
const HOGAN_HPI_TEMPLATE = `You are drafting Hogan Personality Inventory (HPI) items for Intervyo. Original recreations only — never copy from real Hogan material.

Each item is a short statement about how the user typically works. They rate Strongly Disagree / Disagree / Agree / Strongly Agree. There's no "right answer" — each item loads onto one or more of the 7 HPI bright-side scales:

  - adjustment              (composure, stress tolerance)
  - ambition                (drive, leadership, achievement)
  - sociability             (energy from others, social comfort)
  - interpersonal-sensitivity (warmth, tact, perceptiveness)
  - prudence                (conscientiousness, restraint)
  - inquisitive             (curiosity, big-picture thinking)
  - learning-approach       (appetite for new skills)

DIFFICULTY isn't really applicable to personality — items should be naturally worded. Use difficulty {{difficulty}} as a guide for how SUBTLE the loading is (1 = obvious which scale, 3 = nuanced).

COUNT: {{count}}
SECTORS for tagging: {{sectorsJson}}

OUTPUT FORMAT — return ONLY a JSON array. Each item:
{
  "prompt": "<a short statement about how the user typically works, 1 sentence>",
  "options": [
    { "id": "sd", "text": "Strongly Disagree" },
    { "id": "d",  "text": "Disagree" },
    { "id": "a",  "text": "Agree" },
    { "id": "sa", "text": "Strongly Agree" }
  ],
  "correct_answer": null,
  "scoring_metadata": {
    "loadings": { "<scale_id>": <weight> },
    "sectors": {{sectorsJson}},
    "difficulty": {{difficulty}},
    "hint": "There's no right answer — answer how you actually behave.",
    "explanation": "<which scale this loads onto and what trait it measures>"
  }
}

CONSTRAINTS:
- Each item must load onto AT LEAST ONE scale with weight 0.7-1.0.
- Optionally a SECONDARY scale with weight 0.2-0.4.
- Use NEGATIVE weights when 'agree' actually indicates LOWER trait (reverse-keyed items, important for catching response style).
- Item language must be natural, first-person, present tense.
- Aim for an even spread across the 7 scales across the batch.
- Output ONLY the JSON array.`;

const HOGAN_HDS_TEMPLATE = `You are drafting Hogan Development Survey (HDS) items for Intervyo. Original recreations only.

HDS measures dark-side derailers — how someone shows up under stress. Each item is a 4-point Likert. The 11 HDS scales are:

  - excitable        (volatile under pressure)
  - skeptical        (mistrustful, paranoid)
  - cautious         (risk-averse, indecisive)
  - reserved         (aloof, withdrawn)
  - leisurely        (passively resistant)
  - bold             (overconfident, won't admit error)
  - mischievous      (rule-bending, charming risk-taker)
  - colorful         (attention-seeking)
  - imaginative      (eccentric, impractical)
  - diligent         (perfectionist, micromanager)
  - dutiful          (deferential, can't push back)

COUNT: {{count}}
SECTORS for tagging: {{sectorsJson}}

OUTPUT FORMAT — return ONLY a JSON array (same shape as HPI).

CONSTRAINTS:
- Items should describe stress-response or under-pressure behaviour.
- Each item loads onto AT LEAST ONE derailer scale with weight 0.7-1.0.
- Reverse-keying is important — some items use 'agree' = derailer present, others 'disagree' = derailer present.
- Aim for spread across all 11 scales.
- Output ONLY the JSON array.`;

// ─── SHL Verify Interactive (numeric input) ──────────────────────────
const SHL_VERIFY_TEMPLATE = `You are drafting SHL Verify Interactive numerical reasoning items for Intervyo. Original recreations only.

This is the newer SHL format where users TYPE the answer in directly (no multi-choice radio buttons). Two interaction widgets: keypad (exact numbers) or slider (estimates with tolerance).

CONTEXT FLAVOUR: {{sectors}}
THEME / TOPIC: {{theme}}
DIFFICULTY: {{difficulty}} — {{difficultyNote}}
COUNT: {{count}}

OUTPUT FORMAT — return ONLY a JSON array. Each item:
{
  "context": "<introduces the chart/table data>",
  "prompt": "<numerical question>",
  "options": [
    { "id": "answer", "text": "<the correct numeric value as a string, no units e.g. '420000' for £420,000>" }
  ],
  "correct_answer": "answer",
  "scoring_metadata": {
    "widget": "<keypad | slider>",
    "expectedValue": <numeric correct answer>,
    "unit": "<one of: GBP | USD | EUR | percent | count | none>",
    "tolerance": <number — only relevant for slider; ±value accepted>,
    "chartType": "<bar | stacked-bar | line | pie | table>",
    "chartConfig": {
      "title": "<chart title>",
      "xKey": "...",
      "yKeys": ["..."],
      "data": [{ "...": "..." }]
    },
    "sectors": {{sectorsJson}},
    "difficulty": {{difficulty}},
    "hint": "<calculation strategy>",
    "explanation": "<full working>"
  }
}

CONSTRAINTS:
- Use 'keypad' for exact-answer questions (counts, money), 'slider' for estimates.
- Slider tolerance ~5% of expectedValue is reasonable; keypad tolerance 0.
- expectedValue must match what the chart data + question imply.
- Output ONLY the JSON array.`;

// ─── Coding (CodeSignal / Codility / HackerRank) ─────────────────────
const CODING_TEMPLATE = `You are drafting coding-test problems for Intervyo. Original recreations only — never copy from real CodeSignal/Codility/HackerRank problems.

Each problem provides a function signature in JavaScript and 3-5 test cases. The user writes a solution in the editor; the engine runs visible + hidden test cases on submit.

CONTEXT: {{sectors}}
DIFFICULTY: {{difficulty}} — {{difficultyNote}}
COUNT: {{count}}

OUTPUT FORMAT — return ONLY a JSON array. Each item:
{
  "prompt": "<problem statement, 2-4 sentences explaining the task>",
  "context": "<input/output specification, edge cases, constraints>",
  "options": [],
  "correct_answer": null,
  "scoring_metadata": {
    "language": "javascript",
    "functionName": "<solution function name e.g. 'twoSum'>",
    "starterCode": "function <name>(<args>) {\\n  // your code here\\n}",
    "testCases": [
      { "input": [/* args array */], "expected": <expected return value>, "visible": true,  "description": "<short label>" },
      { "input": [/* args array */], "expected": <...>,                  "visible": true,  "description": "..." },
      { "input": [/* args array */], "expected": <...>,                  "visible": false, "description": "edge case: empty input" },
      { "input": [/* args array */], "expected": <...>,                  "visible": false, "description": "edge case: large input" }
    ],
    "sectors": {{sectorsJson}},
    "difficulty": {{difficulty}},
    "topics": ["<topic tags e.g. arrays, strings, hash-maps, two-pointers, dp>"],
    "hint": "<algorithmic approach hint>",
    "explanation": "<full solution explanation with O() complexity>"
  }
}

CONSTRAINTS:
- All test cases must be VERIFIABLY correct — actually run the expected solution mentally to confirm.
- Visible test cases (2-3): cover the happy path so users can debug.
- Hidden test cases (1-2): cover edge cases (empty input, single element, large input, boundary conditions).
- Difficulty 1: classic warm-up problems (FizzBuzz, reverse a string, sum array). Difficulty 2: medium (two-sum, valid parens, longest substring). Difficulty 3: hard (subarray sums, sliding window, basic DP).
- Output ONLY the JSON array.`;

// ─── Public API ───────────────────────────────────────────────────────

export interface BuildPromptOptions extends PromptArgs {
  /** Watson-Glaser only — pick which of the 5 sections this batch is for. */
  watsonSection?: WatsonGlaserSection;
}

export function buildPromptForTest(opts: BuildPromptOptions): string {
  const sectorsDescription = describeSectors(opts.sectors);
  const sectorsForJson = JSON.stringify(opts.firmTag ? [...opts.sectors, `firm-${opts.firmTag}`] : opts.sectors);
  const difficultyNote = DIFFICULTY_NOTES[opts.difficulty];

  const subs: Record<string, string> = {
    sectors:        sectorsDescription,
    sectorsJson:    sectorsForJson,
    theme:          opts.theme || 'general workplace context',
    difficulty:     String(opts.difficulty),
    difficultyNote,
    count:          String(opts.count),
  };

  let template: string;
  if (opts.testSlug.startsWith('sjt-')) {
    template = SJT_TEMPLATE;
  } else if (opts.testSlug === 'verbal-watson-glaser-v1') {
    const section = opts.watsonSection ?? 'inference';
    const cfg = WG_SECTIONS[section];
    subs.section = cfg.name;
    subs.sectionRules = cfg.rules.join('\n');
    subs.sectionOptions = cfg.options;
    template = WATSON_TEMPLATE;
  } else if (opts.testSlug === 'numerical-cappfinity-v1') {
    template = CAPPFINITY_NUM_TEMPLATE;
  } else if (opts.testSlug === 'verbal-cappfinity-v1') {
    template = CAPPFINITY_VRB_TEMPLATE;
  } else if (opts.testSlug === 'numerical-talentq-v1') {
    template = TALENTQ_NUM_TEMPLATE;
  } else if (opts.testSlug === 'verbal-talentq-v1') {
    template = TALENTQ_VRB_TEMPLATE;
  } else if (opts.testSlug === 'logical-talentq-v1') {
    template = TALENTQ_LOG_TEMPLATE;
  } else if (opts.testSlug === 'personality-hogan-hpi-v1') {
    template = HOGAN_HPI_TEMPLATE;
  } else if (opts.testSlug === 'personality-hogan-hds-v1') {
    template = HOGAN_HDS_TEMPLATE;
  } else if (opts.testSlug === 'numerical-shl-interactive-v1') {
    template = SHL_VERIFY_TEMPLATE;
  } else if (opts.testSlug.startsWith('coding-')) {
    template = CODING_TEMPLATE;
  } else {
    template = `Test slug "${opts.testSlug}" doesn't have an authoring template yet. Add one in src/lib/psychometric/promptTemplates.ts.`;
  }

  // Substitute placeholders.
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => subs[key] ?? `{{${key}}}`);
}

/** List the test slugs we support authoring for. UI uses this to populate
 *  the test picker. Add new slugs here as templates are written. */
export const SUPPORTED_AUTHORING_SLUGS: { slug: string; label: string }[] = [
  // ─── SJT ───
  { slug: 'sjt-bespoke-v1',         label: 'SJT — banking scenarios (most/least)' },
  { slug: 'sjt-shl-v1',             label: 'SJT — SHL Most/Least Likely' },
  // ─── Reasoning ───
  { slug: 'verbal-watson-glaser-v1', label: 'Watson-Glaser — Critical Thinking' },
  { slug: 'numerical-cappfinity-v1', label: 'Cappfinity Stage — Numerical' },
  { slug: 'verbal-cappfinity-v1',    label: 'Cappfinity Stage — Verbal' },
  { slug: 'numerical-talentq-v1',    label: 'Talent Q Elements — Numerical (adaptive)' },
  { slug: 'verbal-talentq-v1',       label: 'Talent Q Elements — Verbal (adaptive)' },
  { slug: 'logical-talentq-v1',      label: 'Talent Q Elements — Logical (adaptive)' },
  { slug: 'numerical-shl-interactive-v1', label: 'SHL Verify Interactive — Numerical' },
  // ─── Personality ───
  { slug: 'personality-hogan-hpi-v1', label: 'Hogan HPI (bright-side, Likert)' },
  { slug: 'personality-hogan-hds-v1', label: 'Hogan HDS (derailers, Likert)' },
  // ─── Coding ───
  { slug: 'coding-codesignal-v1',    label: 'Coding — CodeSignal style' },
  { slug: 'coding-codility-v1',      label: 'Coding — Codility style' },
  { slug: 'coding-hackerrank-v1',    label: 'Coding — HackerRank style' },
];
